



















































import OlbIcon from '../icon/icon.vue';
import { defineComponent, reactive, computed, ref, onMounted } from '@vue/composition-api';
import Vue from 'vue';
import VTooltipDefault, { VPopover, VClosePopover } from 'v-tooltip';
import { VNode } from 'vue/types/umd';
import { ResponsiveUtils } from '../../utils';

Vue.component('v-popover', VPopover);
Vue.directive('close-popover', VClosePopover);
Vue.use(VTooltipDefault, { disposeTimeout: 200 });

export default defineComponent({
	props: {
		error: {
			type: Boolean,
			required: false,
			default: false,
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
		shownOptions: {
			type: Number,
			required: false,
			default: 3,
		},
		showAllOptions: {
			type: Boolean,
			required: false,
			default: false,
		},
		primary: {
			type: Boolean,
			required: false,
			default: false,
		},
		placeholder: {
			type: String,
			required: false,
		},
		/**
		 * If true, the first option of the handed slots is chosen as default placeholder and default value, if none is given
		 */
		setDefaultValue: {
			type: Boolean,
			required: false,
			default: true,
		},
		optionsClass: {
			type: String,
			required: false,
		},
		/**
		 * If true, the trigger's size is (for large viewports) adjusted have the width of
		 * its content
		 */
		autosize: {
			type: Boolean,
			required: false,
			default: false,
		},
		required: {
			type: Boolean,
			required: false,
			default: false,
		},
		name: {
			type: String,
			required: false,
			default: 'selectInput',
		},
		inputClass: {
			type: String,
			required: false,
			default: 'form-input js-validate-input hidden',
		},
		dataErrorMsg: {
			type: String,
			required: false,
			default: 'Bitte füllen Sie dieses Feld aus',
		},
		id: {
			type: String,
			required: false,
		},
	},
	components: {
		OlbIcon,
	},
	setup(props, { slots, emit }) {
		const responsiveUtils = new ResponsiveUtils();
		const state = reactive({
			selected: {
				text: null as string | null, // visible text, is set onMounted by setInitialPlaceholder
				value: '' as string | null,
				clientHeight: 0,
				index: 0,
			},
			// necessary due to the fact, that in IE11, update() of the Popover is fired many times during scrolling.
			// This leads to the scroll position being evaluated every time and thus the user is unable to scroll at all
			hasBeenScrolled: false,
			hasBeenResized: false,
			popoverPlacement: null,
		});
		const scrollContainer = ref();
		const selectTrigger = ref();
		const selectTriggerWrapper = ref();
		const selectValInput = ref();
		let options: VNode[] = [];

		function getTextWidth(content: string | null, fontSize: string | null, fontFamily: string | null) {
			if (content === null) {
				return 0;
			}
			const text = document.createElement('span');
			document.body.appendChild(text);

			text.style.fontFamily = fontFamily ? fontFamily : 'arial';
			text.style.fontSize = fontSize ? fontSize : '15px';
			text.style.height = 'auto';
			text.style.width = 'auto';
			text.style.position = 'absolute';
			text.style.whiteSpace = 'no-wrap';
			text.innerHTML = content.trim();

			const formattedWidth = Math.ceil(text.clientWidth);
			document.body.removeChild(text);
			return formattedWidth;
		}

		function css(element: HTMLElement | null, property: string) {
			const el = element && window.getComputedStyle(element, null);
			return el ? el.getPropertyValue(property) : null;
		}

		/**
		 * If autosize is set:
		 * Determine which of the options given has the longest text
		 * in order to set the options popover width accordingly.
		 */
		function getLongestOption() {
			let longestOptionText = '';
			let longestOptionNode = null as null | HTMLElement;
			for (let i = 0; i < scrollContainer.value.childNodes.length; i++) {
				const optionNode = scrollContainer.value.childNodes[i];
				const optionText = optionNode.textContent;
				if (optionText.length > longestOptionText.length) {
					longestOptionText = optionText;
					longestOptionNode = optionNode;
				}
			}
			// Determine the options length based on its text length plus its padding
			const fontFamily = css(longestOptionNode, 'font-family');
			const fontSize = css(longestOptionNode, 'font-size');
			const paddingLeft = css(longestOptionNode, 'padding-left')?.split('px')[0];
			const paddingRight = css(longestOptionNode, 'padding-right')?.split('px')[0];
			return (
				getTextWidth(longestOptionText, fontSize, fontFamily) +
				(paddingLeft ? parseInt(paddingLeft) : 0) +
				(paddingRight ? parseInt(paddingRight) : 0)
			);
		}

		function setInitialPlaceholder() {
			if (props.placeholder) {
				state.selected.text = props.placeholder;
				return;
			}
			// if no placeholder is given:
			// set the first option value as default selected one
			if (props.setDefaultValue && options.length) {
				const firstSlotElm = options[0].elm as HTMLElement;
				const firstSlotText = firstSlotElm && firstSlotElm?.textContent?.trim();
				const firstSlotValue = firstSlotElm && firstSlotElm?.getAttribute('value');
				if (typeof firstSlotText === 'string') {
					state.selected.text = firstSlotText;
					state.selected.value = firstSlotValue;
				}
			}
		}

		/**
		 * Set the trigger width according to its content. For large viewports only.
		 */
		function autoSizeTriggerWidth() {
			const triggerWrapper = selectTrigger.value.$el.querySelector('.trigger') as HTMLElement;
			if (props.autosize && responsiveUtils.isLgUp() && !state.hasBeenResized) {
				triggerWrapper.classList.remove('w-full');
				const triggerValue =
					triggerWrapper.querySelector('.olb-select-value') &&
					(triggerWrapper.querySelector('.olb-select-value') as HTMLElement);
				const trigger = selectTriggerWrapper.value.$el as HTMLElement;

				const paddingLeft = css(trigger, 'padding-left')?.split('px')[0];
				const paddingRight = css(trigger, 'padding-right')?.split('px')[0];
				const fontSize = css(triggerValue, 'font-size');
				const fontFamily = css(triggerValue, 'font-family');
				// set the width according to the trigger's text content
				// plus 15px space for the arrow icon itself
				// plus 15px for some space between text and arrow icon
				// plus any padding from the trigger
				const triggerTextWidth = getTextWidth(state.selected.text, fontSize, fontFamily);
				const autosizedWidth =
					triggerTextWidth +
					15 +
					15 +
					(paddingLeft ? parseInt(paddingLeft) : 0) +
					(paddingRight ? parseInt(paddingRight) : 0);
				// allow a max. width of 100% of the screen
				triggerWrapper.setAttribute('style', `width: ${autosizedWidth}px; max-width: 100%`);
				state.hasBeenResized = true;
			} else if (responsiveUtils.isLgDown() || !props.autosize) {
				triggerWrapper.classList.add('w-full');
				state.hasBeenResized = false;
			}
		}

		function highlightOption(index: number) {
			const highlightedOption = options[index].elm as HTMLElement;
			for (let i = 0; i < options.length; i++) {
				const element = options[i].elm as HTMLElement;
				element.classList?.remove('bg-gray-100');
			}
			!props.primary && highlightedOption.classList.add('bg-gray-100');
		}

		function setSelected(event: Event | undefined, index: number) {
			state.hasBeenScrolled = false;
			const selectedOption: HTMLElement | null = event?.target as HTMLElement;
			if (selectedOption !== null) {
				state.selected.text = selectedOption?.textContent && selectedOption?.textContent.trim();
				state.selected.value = selectedOption?.getAttribute('value');
				state.selected.clientHeight = selectedOption.clientHeight;
				state.selected.index = index;
			}
			highlightOption(index);
			// resize the trigger according to the newly selected option
			if (props.autosize) {
				state.hasBeenResized = false;
				autoSizeTriggerWidth();
			}
		}

		if (props.autosize) {
			window.addEventListener('resize', autoSizeTriggerWidth);
		}

		function focusOption(index: number) {
			const nextOptionElm = options && (options[index].elm as HTMLElement);
			nextOptionElm.focus();
			highlightOption(index);
		}

		function optionKeyBoardEvent(event: KeyboardEvent | undefined, index: number) {
			if (event?.key === 'ArrowDown') {
				event.preventDefault();
				// if there are no more options to focus, stay with the last one
				index === options.length - 1 ? null : index++;
				focusOption(index);
			} else if (event?.key === 'ArrowUp') {
				event.preventDefault();
				// if there are no more options to focus, stay with the first one
				index === 0 ? null : index--;
				focusOption(index);
			} else if (event?.key === 'Escape' || event?.key === ' ' || event?.key === 'Tab') {
				event.preventDefault();
				selectTrigger.value.hide();
				selectTriggerWrapper.value.focus();
			} else if (event?.key === 'Enter') {
				selectTrigger.value.hide();
				selectTriggerWrapper.value.focus();
				setSelected(event, index);
			}
		}

		onMounted(() => {
			options = [...slots.default().filter((vnode: VNode) => vnode.tag !== undefined)];

			// set onclick and keyboard listeners to every option
			for (let i = 0; i < options.length; i++) {
				const element = options[i].elm as HTMLElement;
				if (element.classList.contains('olb-option')) {
					element.addEventListener('click', (event: Event) => setSelected(event, i));
					element.addEventListener('keydown', (event: KeyboardEvent) => optionKeyBoardEvent(event, i));
				}
			}
			setInitialPlaceholder();
			// wait until fonts are loaded before calculating trigger width
			if ((document as any).fonts !== undefined) {
				(document as any).fonts.ready.then(function() {
					autoSizeTriggerWidth();
				});
			} else {
				autoSizeTriggerWidth();
			}
		});

		function getShownOptions(): number {
			if (props.showAllOptions) {
				return options.length;
			}
			const noOfOptions = options.length;
			return props.shownOptions > noOfOptions ? noOfOptions : props.shownOptions;
		}

		function calculateActiveOptionOffset(activeOptionIndex: number): number {
			let offset = 0;
			for (let i = 0; i < activeOptionIndex; i++) {
				const element = options[i].elm as HTMLElement;
				offset += element.clientHeight;
			}
			return offset;
		}

		function getOptionScrollPosition(): number {
			if (state.selected.text === null) {
				return 0;
			}
			const activeOptionIndex = state.selected.index === null ? 0 : state.selected.index;
			const popoverHeight = scrollContainer.value.clientHeight;
			const optionHeight = state.selected.clientHeight;

			const optionOffset = calculateActiveOptionOffset(activeOptionIndex);

			if (optionOffset + optionHeight > popoverHeight) {
				return Math.max(0, optionOffset);
			}
			return 0;
		}

		function frac(val: any, n = 1) {
			if (n > 1) {
				return Math.round(val * n) / n;
			}
			if (n === 1) {
				return val || 0;
			}
			return val;
		}

		const popperOptions = computed(() => ({
			modifiers: {
				// modified solution to fix the 1px offset (from https://github.com/popperjs/popper-core/issues/764#issuecomment-475521005)
				computeStyle: {
					order: 870,
					enabled: true,
					fn(data: Record<string, any>) {
						const pop = data.offsets.popper;

						let x = pop['left'];
						const y = pop['top'];

						const dpr = window.devicePixelRatio;
						const gpu = ![x, y].some((n) => Math.abs(n - frac(n, dpr)) > 0.001);
						x = Math.floor(x);

						const styles = gpu
							? { transform: `translate3d(${x}px, ${y}px, 0)`, top: 0, left: 0, willChange: 'transform' }
							: { top: y, left: x, willChange: 'top, left' };

						data.attributes = {
							'x-placement': data.placement,
							...data.attributes,
						};
						data.styles = { ...data.styles, ...styles };
						data.arrowStyles = { ...data.offsets.arrow, ...data.arrowStyles };
						return data;
					},
				},
				// fetch the opening direction of the dropdown for styling reasons
				addPlacementClass: {
					order: 899,
					enabled: true,
					fn: (data: Record<string, any>) => {
						state.popoverPlacement = data.placement;
						return data;
					},
				},
				// correctly size the popover's width
				autoSizing: {
					order: 860,
					enabled: true,
					fn: (data: Record<string, any>) => {
						data.offsets.popper.left = data.offsets.reference.left;
						// if autosize is set: set the popover's width according to the longest option
						if (props.autosize && responsiveUtils.isLgUp()) {
							data.styles.width = getLongestOption() + 20;
						}
						// otherwise: set the popover's width according to the trigger's
						else {
							data.styles.width = data.offsets.reference.width;
						}
						data.offsets.popper.right = data.offsets.reference.right;
						return data;
					},
				},
				scrollActiveOptionIntoView: {
					order: 850,
					enabled: true,
					fn: (data: Record<string, any>) => {
						if (!state.hasBeenScrolled) {
							scrollContainer.value.scrollTop = getOptionScrollPosition();
							state.hasBeenScrolled = true;
						}
						return data;
					},
				},
				setInitialHeight: {
					order: 900,
					enabled: true,
					fn: (data: Record<string, any>) => {
						let activeOptionOffset = calculateActiveOptionOffset(getShownOptions());
						activeOptionOffset === 0 && (activeOptionOffset = 100);
						scrollContainer.value.style.maxHeight = `${activeOptionOffset + 1}px`;
						return data;
					},
				},
			},
		}));

		function selectClickHandler(event: Event) {
			emit('click', event);
			state.hasBeenScrolled = false;
		}

		function selectCloseHandler(event: Event) {
			emit('close', event);
			// emit keyup event to trigger jquery validation
			let keyupEvent;
			// if phrase necessary due to IE11 compatibility (see https://stackoverflow.com/questions/27176983/dispatchevent-in-chrome-but-not-in-ie11)
			if (typeof Event === 'function') {
				keyupEvent = new Event('keyup', { bubbles: true });
			} else {
				keyupEvent = document.createEvent('Event');
				keyupEvent.initEvent('keyup', true, true);
			}
			selectValInput.value.dispatchEvent(keyupEvent);
		}

		function toggleSelect() {
			if (selectTrigger.value.isOpen) {
				selectTrigger.value.hide();
			} else {
				selectTrigger.value.show();
				// wait for the popover's transition to be completed until focusing on options
				setTimeout(() => {
					focusOption(state.selected.index);
				}, 50);
			}
		}

		return {
			state,
			popperOptions,
			scrollContainer,
			selectClickHandler,
			selectCloseHandler,
			selectTrigger,
			selectTriggerWrapper,
			selectValInput,
			toggleSelect,
		};
	},
});
